import { useMemo } from 'react';
import Modal from '@shared/components/atoms/Modal/Modal';
import { formatSimpleDate } from '@shared/utils/formatDate';
import safeToBase64Image from '@shared/lib/safeToBase64Image';

import styles from '@pages/vacation/components/VacationCommonModal/VacationCommonModal.module.scss';

export default function VacationCommonModal({
  isOpen,
  onClose,
  vacation,
  vacationData,
  vacationStatus,
  allList = [],
  footer,
  onFileClick,
}) {
  // 중복 휴가 계산
  const overlappedItems = useMemo(() => {
    if (!vacationData || !allList.length) return [];

    const currentStart = vacationData.start_date;
    const currentEnd = vacationData.end_date;
    const currentEnumber = vacation.writerInfo?.enumber;
    const currentIdx = vacation.idx;

    return allList.filter((item) => {
      if (item.idx === currentIdx) return false;
      if (item.writerInfo?.enumber !== currentEnumber) return false;
      // 진행중(0)·반려(4)·삭제(5)·관리자취소(7) 문서는 유효한 휴가가 아니므로 중복 카운트에서 제외
      if (item.state === 0 || item.state === 4 || item.state === 5 || item.state === 7) {
        return false;
      }

      return currentStart <= item.end_date && currentEnd >= item.start_date;
    });
  }, [vacationData, allList, vacation]);

  // 서명 이미지 변환
  const { writerBase64, signerBase64 } = useMemo(() => {
    if (!vacationData) {
      return { writerBase64: null, signerBase64: null };
    }

    return {
      writerBase64: vacationData.writer_sign ? safeToBase64Image(vacationData.writer_sign) : null,
      signerBase64: vacationData.signer_sign ? safeToBase64Image(vacationData.signer_sign) : null,
    };
  }, [vacationData]);

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} showCloseButton closeOnOverlayClick={false}>
      {!vacationData ? (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingMessage}>데이터가 없습니다.</div>
        </div>
      ) : (
        <>
          {vacationData.state == 7 && (
            <div className={styles.overlapWarning}>관리자 취소 처리 된 문서입니다.</div>
          )}
          <div className={styles.modalContent}>
            {/* 중복 신청 경고 */}
            {overlappedItems.length > 0 && (
              <div className={styles.overlapWarning}>
                동일 기간 휴가가 {overlappedItems.length}건 존재합니다.
              </div>
            )}

            {/* ================= 헤더 영역 ================= */}
            <div className={styles.headerContainer}>
              <div className={styles.headerBox}>
                <div className={styles.headerTitle}>휴가신청서</div>

                <div className={styles.approverContainer}>
                  {/* 신청자 */}
                  <div className={styles.signerBox}>
                    <span className={styles.signLabel}>기안자</span>
                    <div className={styles.signContent}>
                      {writerBase64 && (
                        <img src={writerBase64} width={38} height={38} alt="신청자 전자서명" />
                      )}
                    </div>
                  </div>

                  {/* 결재자 */}
                  <div className={styles.signerBox}>
                    <span className={styles.signLabel}>결재자</span>
                    <div className={styles.signContent}>
                      {signerBase64 ? (
                        <img src={signerBase64} width={38} height={38} alt="결재자 전자서명" />
                      ) : (
                        '-'
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ================= 바디 영역 ================= */}
            <div className={styles.bodyContainer}>
              <table className={styles.tableContainer}>
                <tbody>
                  <tr>
                    <td>소속</td>
                    <td>{vacationData.writerInfo.team}</td>
                  </tr>
                  <tr>
                    <td>성명</td>
                    <td>{vacationData.writerInfo.name}</td>
                  </tr>
                  <tr>
                    <td>신청내역</td>
                    <td>
                      {vacationData.vacation_type.name || '-'} -
                      {vacationData.cc_type?.name ? ` ${vacationData.cc_type.name}` : ''}
                    </td>
                  </tr>
                  <tr>
                    <td>휴가기간</td>
                    <td>
                      {formatSimpleDate(vacationData.start_date)} ~
                      {formatSimpleDate(vacationData.end_date)} ({vacationData.period}일)
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="2" className={styles.reason}>
                      <div className={styles.reasonContent}>{vacationData.reason}</div>
                    </td>
                  </tr>
                  <tr>
                    <td>첨부파일</td>
                    <td>
                      {Array.isArray(vacationData.fileList) && 0 < vacationData.fileList.length
                        ? vacationData.fileList.map((file, index) => (
                            <div key={index}>
                              <button
                                onClick={() => onFileClick?.(file)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.fileLink}
                              >
                                {file.name}
                              </button>
                            </div>
                          ))
                        : '없음'}
                    </td>
                  </tr>
                  <tr>
                    <td>참조자</td>
                    <td>
                      <div className={styles.recipientList}>
                        {Array.isArray(vacationData.recipientInfos) &&
                        0 < vacationData.recipientInfos.length
                          ? vacationData.recipientInfos.map((r) => r.name).join(', ')
                          : '없음'}
                      </div>
                    </td>
                  </tr>
                  {vacationData.reject_reason && (
                    <tr>
                      <td>코멘트</td>
                      <td>{vacationData.reject_reason}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              <div className={styles.statusTableContainer}>
                <table className={styles.statusTable}>
                  <thead>
                    <tr>
                      <th colSpan="3">발생분</th>
                      <th colSpan="2">사용분(반영)</th>
                      <th colSpan="2">잔여분</th>
                    </tr>
                    <tr>
                      <th>년초 발생분</th>
                      <th>
                        개정 후<br /> 발생분
                      </th>
                      <th>당해 추가분</th>
                      <th>
                        전년
                        <br /> 선사용분
                      </th>
                      <th>당해 사용분</th>
                      <th>개정 후</th>
                      <th className={styles.bold}>총 잔여분</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{vacationStatus.generated.beginningOfYear}</td>
                      <td>{vacationStatus.generated.afterRevision}</td>
                      <td>{vacationStatus.generated.currentYearAdditional}</td>
                      <td>{vacationStatus.used.previousYearPreUsed}</td>
                      <td>{vacationStatus.used.currentYearUsed}</td>
                      <td>{vacationStatus.remaining.afterRevision}</td>
                      <td className={styles.bold}>{vacationStatus.remaining.total}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className={styles.bottomContainer}>
                <div className={styles.bottomBox}>
                  <div className={styles.bottomTitle}>위와 같이 휴가를 신청합니다.</div>
                  <div className={styles.bottomContent}>
                    {formatSimpleDate(vacationData.date, { style: 'korean' })}
                    <br />
                    신청인 : {vacationData.writerInfo.name}
                  </div>
                </div>
                <div className={styles.bottomCompany}>
                  (주) {vacationData.writerInfo.company}귀중
                </div>
              </div>
            </div>
          </div>
          {/* ================= 하단 액션 영역 ================= */}
          {footer && <div className={styles.approvalBtn}>{footer}</div>}
        </>
      )}
    </Modal>
  );
}
